import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { LayoutDashboard, MessageSquareText, FileText, Scale, User, ShieldCheck, LogOut, Plus, Menu, X, Crown } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Tableau de bord", short: "Accueil", icon: LayoutDashboard },
  { to: "/chat", label: "Assistant IA", short: "Assistant", icon: MessageSquareText },
  { to: "/documents", label: "Mes documents", short: "Documents", icon: FileText },
  { to: "/recherche", label: "Recherche juridique", short: "Recherche", icon: Scale },
  { to: "/profil", label: "Profil", short: "Profil", icon: User },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <div className="min-h-screen bg-bone">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-navy text-white flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="Droits IA.com" className="h-8 w-8 object-contain bg-white rounded-md p-0.5" />
          <span className="font-serif text-lg font-semibold">Droits IA.com</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="p-2 -mr-2" aria-label="Ouvrir le menu">
          <Menu size={22} />
        </button>
      </div>

      {/* Overlay */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={close} data-testid="sidebar-overlay" />}

      {/* Sidebar (drawer on mobile, fixed on desktop) */}
      <aside
        className={`w-64 bg-navy text-white flex flex-col fixed h-screen z-50 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Droits IA.com" className="h-9 w-9 object-contain bg-white rounded-md p-0.5" />
            <div>
              <p className="font-serif text-xl leading-none font-semibold">Droits IA.com</p>
              <p className="text-[10px] text-white/50 tracking-wider uppercase mt-1">Assistant juridique</p>
            </div>
          </div>
          <button className="lg:hidden p-1 text-white/70" onClick={close} aria-label="Fermer le menu"><X size={20} /></button>
        </div>

        <div className="p-4">
          <button
            data-testid="new-conversation-btn"
            onClick={() => { navigate("/chat"); close(); }}
            className="w-full flex items-center justify-center gap-2 bg-royal hover:bg-royal/90 text-white py-2.5 rounded-md font-medium text-sm transition-all duration-200"
          >
            <Plus size={16} /> Nouvelle conversation
          </button>
        </div>

        <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={close}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive ? "bg-white/10 text-white font-medium" : "text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <item.icon size={18} /> {item.label}
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin"
              onClick={close}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive ? "bg-gold/20 text-gold font-medium" : "text-gold/70 hover:text-gold hover:bg-gold/10"
                }`
              }
            >
              <ShieldCheck size={18} /> Administration
            </NavLink>
          )}
          <NavLink
            to="/premium"
            onClick={close}
            data-testid="nav-premium"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                isActive ? "bg-gold/20 text-gold font-medium" : "text-gold/80 hover:text-gold hover:bg-gold/10"
              }`
            }
          >
            <Crown size={18} /> Passer à Premium
          </NavLink>
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-royal flex items-center justify-center text-xs font-semibold">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </div>
            )}
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center gap-2 text-white/60 hover:text-white text-sm px-1 transition-colors"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0 pb-16 lg:pb-0 relative">
        {/* Logo watermark background */}
        <div aria-hidden className="pointer-events-none fixed inset-0 lg:left-64 z-0 flex items-center justify-center overflow-hidden">
          <img src={LOGO_URL} alt="" className="w-[min(80vw,700px)] max-w-none opacity-[0.035] select-none" />
        </div>
        <div className="relative z-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom navigation */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 h-16 bg-white border-t border-border flex items-stretch" data-testid="bottom-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`bottomnav-${item.to.replace("/", "")}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
                isActive ? "text-royal font-semibold" : "text-slate-400"
              }`
            }
          >
            <item.icon size={20} />
            {item.short}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
